/**
 * JavaScript XGBoost Predictor for Soccer
 *
 * Evaluates XGBoost models exported in JSON format directly in the browser.
 * Soccer-specific: tracks ball position only (NO posture model).
 *
 * Uses velocity extrapolation for missing detections (like pickleball).
 * ByteTrack-compatible: uses StreamingDetectionPredictor for efficient O(1) updates.
 *
 * Features per frame (9 total):
 * - ball_x, ball_y (2) - normalized position
 * - ball_vx, ball_vy (2) - velocity components
 * - ball_speed (1) - velocity magnitude
 * - ball_ax, ball_ay (2) - acceleration components
 * - ball_acceleration (1) - acceleration magnitude
 * - ball_is_interpolated (1) - 1 if predicted, 0 otherwise
 *
 * Window-level feature (1):
 * - peak_ball_acceleration (1) - highest ball acceleration within ±2 frames
 *
 * Total: 9 * 9 frames + 1 = 82 features
 */

import { XGBoostPredictor } from './xgboostPredictor.js';
import {
  isValidDetection,
  getBallDetection,
  StreamingDetectionPredictor,
  getDetectionAtFrame,
  buildPredictionsForRange,
  DEFAULT_MAX_ANCESTOR_DEPTH
} from './soccerInterpolation.js';

// Constants for feature extraction
export const SOCCER_TEMPORAL_WINDOW = 4;

// Features per frame (9 total - ball only, no posture)
const SOCCER_FEATURES_PER_FRAME = 9;

// Window-level features (1 total):
// - peak_ball_acceleration: highest ball acceleration within ±2 frames
const WINDOW_FEATURES = 1;

// Re-export for convenience
export { StreamingDetectionPredictor, DEFAULT_MAX_ANCESTOR_DEPTH };

/**
 * Compute velocity vector { vx, vy, magnitude }
 * Returns null if invalid
 */
function computeVelocityVector(current, prev, frameW, frameH) {
  if (!current || !prev) return null;

  const vx = (current.x - prev.x) / frameW;
  const vy = (current.y - prev.y) / frameH;
  const magnitude = Math.sqrt(vx * vx + vy * vy);
  return { vx, vy, magnitude };
}

/**
 * Compute vector acceleration: { dvx, dvy, magnitude }
 * This captures direction changes, not just speed changes
 * Returns null if invalid inputs
 */
function computeVectorAcceleration(v1, v2) {
  if (!v1 || !v2) return null;
  const dvx = v2.vx - v1.vx;
  const dvy = v2.vy - v1.vy;
  const magnitude = Math.sqrt(dvx * dvx + dvy * dvy);
  return { dvx, dvy, magnitude };
}

/**
 * Find peak ball acceleration within ±2 frames of center
 * Uses vector acceleration to capture direction changes
 *
 * @param {Array} frameBuffer - Array of frame objects
 * @param {number} centerIdx - Index of center frame
 * @param {number} frameW - Frame width
 * @param {number} frameH - Frame height
 * @param {Object} predictions - Optional dict of {idx: {ball: detection}} from streaming predictor
 */
function findPeakBallAcceleration(frameBuffer, centerIdx, frameW, frameH, predictions = null) {
  // Helper to get ball - use predictions if available, otherwise raw frame
  const getBallAt = (idx) => {
    if (predictions && predictions[idx]) {
      return predictions[idx].ball;
    }
    if (idx >= 0 && idx < frameBuffer.length && frameBuffer[idx]) {
      return getBallDetection(frameBuffer[idx]);
    }
    return null;
  };

  // Collect velocity vectors for frames in range [center-3, center+2]
  const velocities = {};

  for (let offset = -3; offset <= 2; offset++) {
    const idx = centerIdx + offset;
    const prevIdx = idx - 1;

    const ball = getBallAt(idx);
    const prevBall = getBallAt(prevIdx);
    if (ball && prevBall) {
      const vel = computeVelocityVector(ball, prevBall, frameW, frameH);
      if (vel) {
        velocities[idx] = vel;
      }
    }
  }

  // Compute accelerations for frames in range [center-2, center+2]
  // acceleration[X] = magnitude(velocity[X] - velocity[X-1])
  let peakAcceleration = -1;

  for (let offset = -2; offset <= 2; offset++) {
    const idx = centerIdx + offset;
    const prevIdx = idx - 1;

    if (velocities[idx] && velocities[prevIdx]) {
      const accel = computeVectorAcceleration(velocities[prevIdx], velocities[idx]);
      if (accel && accel.magnitude > peakAcceleration) {
        peakAcceleration = accel.magnitude;
      }
    }
  }

  return peakAcceleration;
}

/**
 * Extract features from a single frame (ball only, no posture)
 *
 * @param {Object} ball - Current ball detection
 * @param {Object} prevBall - Previous ball detection
 * @param {Object} prevPrevBall - Ball detection from 2 frames ago
 * @param {boolean} ballInterpolated - Whether ball was interpolated
 * @param {number} frameW - Frame width
 * @param {number} frameH - Frame height
 * @returns {Array} Feature array (9 features)
 */
function frameToFeatures(ball, prevBall, prevPrevBall, ballInterpolated, frameW, frameH) {
  const features = [];

  // Ball position (normalized, -1 if missing)
  if (ball) {
    features.push(ball.x / frameW);
    features.push(ball.y / frameH);
  } else {
    features.push(-1, -1);
  }

  // Ball velocity components and speed
  let ballVx = -1, ballVy = -1, ballSpeed = -1;
  if (ball && prevBall) {
    ballVx = (ball.x - prevBall.x) / frameW;
    ballVy = (ball.y - prevBall.y) / frameH;
    ballSpeed = Math.sqrt(ballVx * ballVx + ballVy * ballVy);
  }
  features.push(ballVx);
  features.push(ballVy);
  features.push(ballSpeed);

  // Ball acceleration components and magnitude
  let ballAx = -1, ballAy = -1, ballAccel = -1;
  if (ballSpeed >= 0 && prevBall && prevPrevBall) {
    const prevBallVx = (prevBall.x - prevPrevBall.x) / frameW;
    const prevBallVy = (prevBall.y - prevPrevBall.y) / frameH;
    ballAx = ballVx - prevBallVx;
    ballAy = ballVy - prevBallVy;
    ballAccel = Math.sqrt(ballAx * ballAx + ballAy * ballAy);
  }
  features.push(ballAx);
  features.push(ballAy);
  features.push(ballAccel);

  // Interpolation flag
  features.push(ballInterpolated ? 1 : 0);

  return features;
}

/**
 * Extract temporal features for XGBoost juggle detection.
 *
 * Total features: (2 * SOCCER_TEMPORAL_WINDOW + 1) * 9 + 1 = 82 features for window=4
 * - 9 features per frame × 9 frames = 81 frame features
 * - 1 window-level feature (peak_ball_acceleration)
 *
 * Uses prediction for missing detections (velocity extrapolation from t-1, t-2).
 * Prediction stops after maxAncestorDepth consecutive missing frames.
 *
 * Four modes:
 * 1. Cached (pass cachedPredictions): Uses pre-computed predictions (fastest, O(1))
 * 2. Streaming (pass streamingPredictor): Uses predictor state for frames before buffer
 * 3. Batch/Random access (pass allFrames + bufferStartIdx): Uses recursive cache
 * 4. Standalone (no options): Processes buffer without history
 *
 * @param {Array} frameBuffer - Array of frame objects with ball detections
 * @param {number} centerIdx - Index of the center frame in the buffer
 * @param {number} frameW - Frame width for normalization (default 640)
 * @param {number} frameH - Frame height for normalization (default 480)
 * @param {Object} options - Optional parameters:
 *   - cachedPredictions: Array of pre-computed predictions parallel to frameBuffer (fastest)
 *   - streamingPredictor: StreamingDetectionPredictor for streaming mode
 *   - allFrames: Full frames array for batch/random access mode
 *   - bufferStartIdx: Start index of buffer in allFrames (for random access)
 *   - maxAncestorDepth: Max prediction chain depth (default: DEFAULT_MAX_ANCESTOR_DEPTH)
 * @returns {Array} Feature array (82 features, always returns, ML handles missing data)
 */
export function extractSoccerTemporalFeatures(frameBuffer, centerIdx, frameW = 640, frameH = 480, options = {}) {
  const features = [];
  const maxAncestorDepth = options.maxAncestorDepth ?? DEFAULT_MAX_ANCESTOR_DEPTH;

  // Build predictions for all frames we need
  const predictions = {};

  if (options.cachedPredictions) {
    // Cached mode: use pre-computed predictions directly (fastest path, O(1))
    // cachedPredictions is parallel to frameBuffer
    for (let bufferIdx = 0; bufferIdx < options.cachedPredictions.length; bufferIdx++) {
      predictions[bufferIdx] = options.cachedPredictions[bufferIdx];
    }
    // Also set predictions[-1] for velocity calculation of the first buffer frame
    // This is the prediction that was shifted out when the buffer became full
    if (options.preBufferPrediction) {
      predictions[-1] = options.preBufferPrediction;
    }
  } else if (options.allFrames && options.bufferStartIdx !== undefined) {
    // Batch/random access mode: use recursive cache with full frames array
    const cache = {};
    const bufferStartIdx = options.bufferStartIdx;

    // Get predictions for all buffer frames and one before (for velocity)
    for (let bufferIdx = -1; bufferIdx < frameBuffer.length; bufferIdx++) {
      const frameIdx = bufferStartIdx + bufferIdx;
      if (frameIdx >= 0 && frameIdx < options.allFrames.length) {
        const ballResult = getDetectionAtFrame(options.allFrames, frameIdx, { maxAncestorDepth, cache });
        predictions[bufferIdx] = {
          ball: ballResult.detection,
          ballWasPredicted: ballResult.wasPredicted
        };
      }
    }
  } else if (options.streamingPredictor) {
    // Streaming mode: use predictor state for frames before buffer
    const predictor = options.streamingPredictor;

    // Get state for frame before buffer (for velocity calculation)
    const prevState = predictor.getPrevState();
    predictions[-1] = {
      ball: prevState.ball,
      ballWasPredicted: false
    };

    // Process buffer frames sequentially using a local predictor
    const localPredictor = new StreamingDetectionPredictor(maxAncestorDepth);
    if (predictor.prevBall) {
      localPredictor.prevBall = { ...predictor.prevBall };
    }
    if (predictor.prevPrevBall) {
      localPredictor.prevPrevBall = { ...predictor.prevPrevBall };
    }

    for (let bufferIdx = 0; bufferIdx < frameBuffer.length; bufferIdx++) {
      const result = localPredictor.processFrame(frameBuffer[bufferIdx]);
      predictions[bufferIdx] = {
        ball: result.ball,
        ballWasPredicted: result.ballWasPredicted
      };
    }
  } else {
    // Standalone mode: process buffer frames like Python does
    // First, determine the buffer range (same as Python's buffer slicing)
    const bufferStart = Math.max(0, centerIdx - SOCCER_TEMPORAL_WINDOW);
    const bufferEnd = Math.min(frameBuffer.length, centerIdx + SOCCER_TEMPORAL_WINDOW + 1);

    // Process frames before buffer to build streaming state
    const localPredictor = new StreamingDetectionPredictor(maxAncestorDepth);
    for (let i = 0; i < bufferStart; i++) {
      localPredictor.processFrame(frameBuffer[i] || {});
    }

    // Get t-1 state from streaming predictor
    const prevState = localPredictor.getPrevState();
    predictions[-1] = {
      ball: prevState.ball,
      ballWasPredicted: false
    };

    // Process buffer frames with buffer-relative indices (0 to buffer_size-1)
    for (let i = bufferStart; i < bufferEnd; i++) {
      const bufferRelativeIdx = i - bufferStart;
      const result = localPredictor.processFrame(frameBuffer[i] || {});
      predictions[bufferRelativeIdx] = {
        ball: result.ball,
        ballWasPredicted: result.ballWasPredicted
      };
    }

    // Adjust centerIdx to buffer-relative index for feature extraction
    options._bufferStart = bufferStart;
    options._useBufferRelativeIdx = true;
  }

  // Extract features for each frame in the window
  for (let offset = -SOCCER_TEMPORAL_WINDOW; offset <= SOCCER_TEMPORAL_WINDOW; offset++) {
    const idx = centerIdx + offset;

    // For standalone mode, use buffer-relative indices for predictions
    let predIdx, prevPredIdx, prevPrevPredIdx;
    if (options._useBufferRelativeIdx) {
      const bufferStart = options._bufferStart;
      predIdx = idx - bufferStart;
      prevPredIdx = predIdx - 1;
      prevPrevPredIdx = predIdx - 2;
    } else {
      predIdx = idx;
      prevPredIdx = idx - 1;
      prevPrevPredIdx = idx - 2;
    }

    if (idx >= 0 && idx < frameBuffer.length) {
      const pred = predictions[predIdx] || {};
      const prevPred = predictions[prevPredIdx] || {};
      const prevPrevPred = predictions[prevPrevPredIdx] || {};

      const ball = pred.ball || null;
      const prevBall = prevPred.ball || null;
      const prevPrevBall = prevPrevPred.ball || null;
      const ballInterpolated = pred.ballWasPredicted || false;

      const frameFeatures = frameToFeatures(
        ball, prevBall, prevPrevBall,
        ballInterpolated, frameW, frameH
      );

      features.push(...frameFeatures);
    } else {
      // Out of bounds - use -1 as sentinel for all features
      for (let i = 0; i < SOCCER_FEATURES_PER_FRAME; i++) {
        features.push(-1);
      }
    }
  }

  // Add window-level feature: peak ball acceleration within ±2 frames of center
  // Use predictions for consistent ball positions with per-frame features
  const peakAccel = findPeakBallAcceleration(frameBuffer, centerIdx, frameW, frameH, predictions);
  features.push(peakAccel);

  return features;
}

/**
 * Create a soccer-specific XGBoost predictor instance
 * @returns {XGBoostPredictor}
 */
export function createSoccerPredictor() {
  return new XGBoostPredictor();
}
